import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calculator, Download, FileText, RefreshCcw, Users, Wallet } from 'lucide-react';

import { hrApi } from '../../../api';

const money = (value) => `Rs ${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const daysText = (value) => `${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })} day(s)`;

const previousMonthValue = () => {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
};

const statusBadge = (status) => {
  const key = String(status || '').toUpperCase();
  if (key === 'PAID') {
    return {
      label: 'PAID IN ACCOUNTS',
      style: { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' },
    };
  }
  if (key === 'PENDING' || key === 'PENDING_APPROVAL') {
    return {
      label: 'PENDING IN ACCOUNTS',
      style: { background: '#ffedd5', color: '#9a3412', border: '1px solid #fdba74' },
    };
  }
  return {
    label: 'NOT GENERATED',
    style: { background: '#e2e8f0', color: '#334155', border: '1px solid #cbd5e1' },
  };
};

function PayrollSection({ title, subtitle, icon: Icon, rows, kind, onDownload }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700">
          <Icon size={14} />
          {rows.length} {kind}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Department</th>
              <th className="px-4 py-3">Base Pay</th>
              <th className="px-4 py-3">Attendance</th>
              <th className="px-4 py-3">Adjustments</th>
              <th className="px-4 py-3">Gross</th>
              <th className="px-4 py-3">Net</th>
              <th className="px-4 py-3">Payment</th>
              <th className="px-4 py-3">Paid At</th>
              <th className="px-4 py-3">Payslip</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const badge = statusBadge(row.payment_status);
              const baseSuffix = row.base_pay_label === 'daily_wage' ? '/day' : '/month';
              return (
                <tr key={`${row.person_type}-${row.id}`} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{row.name || '-'}</div>
                    <div className="mt-1 text-xs text-slate-500">{row.branch_name || row.email || row.contact_phone || '-'}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{row.code || '-'}</td>
                  <td className="px-4 py-3 text-slate-700">{row.role || '-'}</td>
                  <td className="px-4 py-3 text-slate-700">{row.department || '-'}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    <div>{money(row.base_pay)}{baseSuffix}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      Rate: {money(row.daily_rate || 0)}{row.base_pay_label === 'daily_wage' ? '/day' : '/day'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <div>{daysText(row.days_worked)} worked</div>
                    <div className="mt-1 text-xs text-slate-500">{daysText(row.lop_days)} leave / LOP</div>
                    <div className="mt-1 text-xs text-slate-500">{daysText(row.extra_paid_days)} paid leave / extra</div>
                    <div className="mt-1 text-xs font-semibold text-slate-700">{daysText(row.payable_days)} payable</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <div>Bonus: {money(row.bonus_amount)}</div>
                    <div className="mt-1 text-xs text-slate-500">OT: {money(row.overtime_amount)} · Extra: {money(row.extra_amount)}</div>
                    <div className="mt-1 text-xs text-slate-500">Incentive: {money(row.incentives)}</div>
                    <div className="mt-1 text-xs font-semibold text-rose-600">LOP Cut: {money(row.lop_amount || row.deductions)}</div>
                  </td>
                  <td className="px-4 py-3">{money(row.gross)}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{money(row.net)}</td>
                  <td className="px-4 py-3">
                    <div className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold" style={badge.style}>
                      {badge.label}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{row.payment_reference || '-'}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {row.paid_at ? new Date(row.paid_at).toLocaleString('en-IN') : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      disabled={!row.payslip_available}
                      onClick={() => onDownload(row)}
                      className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                    >
                      <Download size={13} /> PDF
                    </button>
                  </td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr>
                <td className="px-4 py-10 text-center text-slate-500" colSpan={12}>
                  No records found for this section.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PayrollScreen({ addToast, canWrite: canWriteProp }) {
  const [tab, setTab] = useState('people');
  const [period, setPeriod] = useState(previousMonthValue());
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [overview, setOverview] = useState({ employees: [], workers: [], summary: {} });
  const [activeRun, setActiveRun] = useState(null);
  const [salarySlips, setSalarySlips] = useState([]);
  const [slipsLoading, setSlipsLoading] = useState(false);

  const fetchRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const res = await hrApi.get('/hr/payroll');
      setRuns(res.data?.data || []);
    } catch (err) {
      addToast?.(err.response?.data?.message || 'Failed to load payroll runs', 'error');
    } finally {
      setRunsLoading(false);
    }
  }, [addToast]);

  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const res = await hrApi.get('/hr/payroll/overview', { params: { period } });
      setOverview(res.data?.data || { employees: [], workers: [], summary: {} });
    } catch (err) {
      setOverview({ employees: [], workers: [], summary: {} });
      addToast?.(err.response?.data?.message || 'Failed to load payroll people overview', 'error');
    } finally {
      setOverviewLoading(false);
    }
  }, [addToast, period]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const calculatePayroll = async () => {
    setCalculating(true);
    try {
      const res = await hrApi.post('/hr/payroll/calculate', { period });
      addToast?.(res.data?.message || 'Payroll calculated', 'success');
      fetchRuns();
      fetchOverview();
    } catch (err) {
      addToast?.(err.response?.data?.message || 'Payroll calculation failed', 'error');
    } finally {
      setCalculating(false);
    }
  };

  const openSlips = async (run) => {
    setActiveRun(run);
    setSlipsLoading(true);
    try {
      const res = await hrApi.get(`/hr/payroll/${run.id}/salary-slips`);
      setSalarySlips(res.data?.data?.salary_slips || []);
    } catch (err) {
      setSalarySlips([]);
      addToast?.(err.response?.data?.message || 'Failed to fetch salary slips', 'error');
    } finally {
      setSlipsLoading(false);
    }
  };

  const downloadSlipPdf = async (runId, lineId, employeeCode) => {
    try {
      const response = await hrApi.get(`/hr/payroll/${runId}/salary-slips/${lineId}/pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `salary_slip_${employeeCode || lineId}_${period}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      addToast?.(err.response?.data?.message || 'Failed to download salary slip PDF', 'error');
    }
  };

  const downloadPersonSlip = (row) => {
    if (!row?.payroll_run_id || !row?.payroll_line_id) return;
    downloadSlipPdf(row.payroll_run_id, row.payroll_line_id, row.code);
  };

  const filteredRuns = useMemo(
    () => runs.filter((run) => !period || String(run.period || '') === period),
    [runs, period]
  );

  const runSummary = useMemo(() => {
    const source = filteredRuns.length ? filteredRuns : runs;
    return source.reduce(
      (acc, run) => {
        acc.totalGross += Number(run.total_gross || 0);
        acc.totalDeductions += Number(run.total_deductions || 0);
        acc.totalNet += Number(run.total_net || 0);
        return acc;
      },
      { totalGross: 0, totalDeductions: 0, totalNet: 0 }
    );
  }, [filteredRuns, runs]);

  const peopleSummary = overview.summary || {};

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Payroll</h2>
            <p className="mt-1 text-sm text-slate-600">
              HR can review payroll details here, but payment is handled only inside the Accountant module.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900"
            />
            <button
              type="button"
              disabled
              className="inline-flex items-center gap-2 rounded-xl bg-slate-300 px-4 py-2 text-sm font-semibold text-white cursor-not-allowed"
              style={{ minHeight: 44 }}
            >
              <Calculator size={16} />
              Payment Done In Accountant
            </button>
            <button
              type="button"
              onClick={() => {
                fetchRuns();
                fetchOverview();
              }}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
            >
              <RefreshCcw size={16} /> Refresh
            </button>
          </div>
        </div>

        <div className="mt-4 inline-flex rounded-xl bg-slate-100 p-1">
          <button
            type="button"
            onClick={() => setTab('people')}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${tab === 'people' ? 'bg-white text-slate-900' : 'text-slate-600'}`}
          >
            Staff & Workers
          </button>
          <button
            type="button"
            onClick={() => setTab('runs')}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${tab === 'runs' ? 'bg-white text-slate-900' : 'text-slate-600'}`}
          >
            Payroll Runs
          </button>
          <button
            type="button"
            onClick={() => setTab('summary')}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${tab === 'summary' ? 'bg-white text-slate-900' : 'text-slate-600'}`}
          >
            Summary
          </button>
        </div>
      </div>

      {tab === 'people' && (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs font-semibold uppercase text-slate-500">Staff</div>
              <div className="mt-2 text-2xl font-bold text-slate-900">{peopleSummary.employees_count || 0}</div>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs font-semibold uppercase text-slate-500">Workers</div>
              <div className="mt-2 text-2xl font-bold text-slate-900">{peopleSummary.workers_count || 0}</div>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs font-semibold uppercase text-slate-500">Paid In Accounts</div>
              <div className="mt-2 text-2xl font-bold text-emerald-700">{peopleSummary.paid_count || 0}</div>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs font-semibold uppercase text-slate-500">Pending / Not Generated</div>
              <div className="mt-2 text-2xl font-bold text-amber-700">
                {(peopleSummary.pending_count || 0) + (peopleSummary.not_generated_count || 0)}
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-blue-200 bg-blue-50 px-5 py-4 text-sm text-blue-900 shadow-sm">
            Payroll for <span className="font-semibold">{overview.period || period}</span> is shown below in separate Staff and Worker sections.
            Payment status reflects whether the Accountant module has marked that month&apos;s payroll as paid.
            Attendance impact, LOP cuts, paid leave days, bonus, OT, incentives, and extra amounts are included in the payslip details below.
          </div>

          {overviewLoading ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center text-slate-500 shadow-sm">
              Loading staff and worker payroll details...
            </div>
          ) : (
            <div className="space-y-6">
              <PayrollSection
                title="Staff Payments"
                subtitle="Monthly salary records with accountant payment status and payslips."
                icon={Users}
                rows={overview.employees || []}
                kind="staff"
                onDownload={downloadPersonSlip}
              />
              <PayrollSection
                title="Worker Payments"
                subtitle="Worker wage-based payroll records with accountant payment status and payslips."
                icon={Wallet}
                rows={overview.workers || []}
                kind="workers"
                onDownload={downloadPersonSlip}
              />
            </div>
          )}
        </>
      )}

      {tab === 'runs' && (
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Period</th>
                  <th className="px-4 py-3">People</th>
                  <th className="px-4 py-3">Gross</th>
                  <th className="px-4 py-3">Deductions</th>
                  <th className="px-4 py-3">Net Pay</th>
                  <th className="px-4 py-3">Payment Status</th>
                  <th className="px-4 py-3">Paid By</th>
                  <th className="px-4 py-3">Paid At</th>
                  <th className="px-4 py-3">Salary Slips</th>
                </tr>
              </thead>
              <tbody>
                {(filteredRuns.length ? filteredRuns : runs).map((run) => {
                  const badge = statusBadge(run.payment_status);
                  return (
                    <tr key={run.id} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-medium text-slate-900">{run.period || '-'}</td>
                      <td className="px-4 py-3">{run.employee_count || 0}</td>
                      <td className="px-4 py-3">{money(run.total_gross)}</td>
                      <td className="px-4 py-3">{money(run.total_deductions)}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900">{money(run.total_net)}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold" style={badge.style}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">{run.paid_by_name || '-'}</td>
                      <td className="px-4 py-3">{run.paid_at ? new Date(run.paid_at).toLocaleString('en-IN') : '-'}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => openSlips(run)}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-700"
                        >
                          <FileText size={14} /> View
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!runsLoading && !(filteredRuns.length ? filteredRuns : runs).length && (
                  <tr>
                    <td className="px-4 py-10 text-center text-slate-500" colSpan={9}>
                      No payroll runs found for this period.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="bg-slate-50 text-sm font-semibold text-slate-800">
                <tr>
                  <td className="px-4 py-3" colSpan={2}>Total</td>
                  <td className="px-4 py-3">{money(runSummary.totalGross)}</td>
                  <td className="px-4 py-3">{money(runSummary.totalDeductions)}</td>
                  <td className="px-4 py-3">{money(runSummary.totalNet)}</td>
                  <td className="px-4 py-3" colSpan={4}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {tab === 'summary' && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-xs font-semibold uppercase text-slate-500">Gross Payroll</div>
            <div className="mt-2 text-2xl font-bold text-slate-900">{money(runSummary.totalGross)}</div>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-xs font-semibold uppercase text-slate-500">Deductions</div>
            <div className="mt-2 text-2xl font-bold text-slate-900">{money(runSummary.totalDeductions)}</div>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-xs font-semibold uppercase text-slate-500">Net Payroll</div>
            <div className="mt-2 text-2xl font-bold text-slate-900">{money(runSummary.totalNet)}</div>
          </div>
        </div>
      )}

      {activeRun && (
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Salary Slips ({activeRun.period || '-'})</h3>
              <p className="text-sm text-slate-500">Per-person salary breakdown with download option.</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setActiveRun(null);
                setSalarySlips([]);
              }}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700"
            >
              Close
            </button>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Attendance</th>
                  <th className="px-4 py-3">Additions / Deductions</th>
                  <th className="px-4 py-3">Gross</th>
                  <th className="px-4 py-3">Deductions</th>
                  <th className="px-4 py-3">Net</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Payment Ref</th>
                  <th className="px-4 py-3">PDF</th>
                </tr>
              </thead>
              <tbody>
                {salarySlips.map((slip) => {
                  const badge = statusBadge(slip.payment_status);
                  return (
                    <tr key={slip.line_id} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-medium text-slate-900">{slip.name || '-'}</td>
                      <td className="px-4 py-3">{slip.employee_code || '-'}</td>
                      <td className="px-4 py-3 text-slate-700">
                        <div>{daysText(slip.days_worked)} worked</div>
                        <div className="mt-1 text-xs text-slate-500">{daysText(slip.lop_days)} LOP</div>
                        <div className="mt-1 text-xs text-slate-500">{daysText(slip.extra_paid_days)} paid extra</div>
                        <div className="mt-1 text-xs font-semibold text-slate-700">{daysText(slip.payable_days)} payable</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div>Bonus: {money(slip.bonus_amount)}</div>
                        <div className="mt-1 text-xs text-slate-500">OT: {money(slip.overtime_amount)} · Extra: {money(slip.extra_amount)}</div>
                        <div className="mt-1 text-xs text-slate-500">Incentive: {money(slip.incentives)}</div>
                        <div className="mt-1 text-xs font-semibold text-rose-600">LOP Cut: {money(slip.lop_amount || slip.deductions)}</div>
                      </td>
                      <td className="px-4 py-3">{money(slip.gross)}</td>
                      <td className="px-4 py-3">{money(slip.deductions)}</td>
                      <td className="px-4 py-3 font-semibold">{money(slip.net)}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold" style={badge.style}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{slip.payment_reference || '-'}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => downloadSlipPdf(activeRun.id, slip.line_id, slip.employee_code)}
                          className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white"
                        >
                          <Download size={13} /> Download
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {slipsLoading && (
                  <tr>
                    <td className="px-4 py-8 text-center text-slate-500" colSpan={10}>Loading salary slips...</td>
                  </tr>
                )}
                {!slipsLoading && !salarySlips.length && (
                  <tr>
                    <td className="px-4 py-8 text-center text-slate-500" colSpan={10}>No salary slips found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default PayrollScreen;
